// src/admin/pages/ShareTypesPage.js
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Activity } from 'lucide-react';
import { supabase } from '../../config';

const ShareTypesPage = () => {
  const [shareTypes, setShareTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingType, setEditingType] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newType, setNewType] = useState({
    name: '',
    description: '',
    priority: 1,
    is_active: true
  });

  const fetchShareTypes = async () => {
    setLoading(true);
    try {
      // Fetch all share types and share classes from the table
      const { data, error } = await supabase
        .from('equity_share_classes')
        .select('*')
        .order('priority', { ascending: true });

      if (error) {
        console.error('Error fetching share types:', error);
        throw error;
      }

      setShareTypes(data || []);
    } catch (error) {
      console.error('Error fetching share types:', error);
      setShareTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShareTypes();
  }, []);

  const handleCreate = async () => {
    if (!newType.name.trim()) {
      alert('Share type name is required');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('equity_share_classes')
        .insert([{
          name: newType.name.trim(),
          description: newType.description.trim(),
          priority: parseInt(newType.priority),
          is_active: newType.is_active,
          company_id: null
        }]);

      if (error) throw error;

      alert('Share type created successfully!');
      setShowCreateModal(false);
      setNewType({ name: '', description: '', priority: 1, is_active: true });
      fetchShareTypes();
    } catch (error) {
      console.error('Error creating share type:', error);
      alert('Error creating share type: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editingType.name.trim()) {
      alert('Share type name is required');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('equity_share_classes')
        .update({
          name: editingType.name.trim(),
          description: editingType.description.trim(),
          priority: parseInt(editingType.priority),
          is_active: editingType.is_active
        })
        .eq('id', id);

      if (error) throw error;

      alert('Share type updated successfully!');
      setEditingType(null);
      fetchShareTypes();
    } catch (error) {
      console.error('Error updating share type:', error);
      alert('Error updating share type: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this share type? This cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('equity_share_classes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      alert('Share type deleted successfully!');
      fetchShareTypes();
    } catch (error) {
      console.error('Error deleting share type:', error);
      alert('Error deleting share type: ' + error.message);
    }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      const { error } = await supabase
        .from('equity_share_classes')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      fetchShareTypes();
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Error updating status: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-full">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading share types...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Share Types</h2>
          <p className="text-gray-600 mt-1">Manage all share types in the system</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Share Type
        </button>
      </div>


      {/* Share Types Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">
            All Share Types ({shareTypes.length})
          </h3>
        </div>
        
        {shareTypes.length === 0 ? (
          <div className="p-12 text-center">
            <Activity className="mx-auto h-16 w-16 text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No share types found</h3>
            <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
              There are no global share type templates in the database yet. 
              Click "Add Share Type" above to create your first template.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-6 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create First Share Type
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {shareTypes.map((type) => (
                  <tr key={type.id} className={`${!type.is_active ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingType?.id === type.id ? (
                        <input
                          type="number"
                          value={editingType.priority}
                          onChange={(e) => setEditingType({ ...editingType, priority: parseInt(e.target.value) })}
                          className="w-20 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="1"
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-900">{type.priority}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingType?.id === type.id ? (
                        <input
                          type="text"
                          value={editingType.name}
                          onChange={(e) => setEditingType({ ...editingType, name: e.target.value })}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-900">{type.name}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {editingType?.id === type.id ? (
                        <input
                          type="text"
                          value={editingType.description || ''}
                          onChange={(e) => setEditingType({ ...editingType, description: e.target.value })}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <span className="text-sm text-gray-900">{type.description || '-'}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {type.company_id ? (
                        <span className="text-sm text-gray-600">Company {type.company_id}</span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Global
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingType?.id === type.id ? (
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editingType.is_active}
                            onChange={(e) => setEditingType({ ...editingType, is_active: e.target.checked })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">Active</span>
                        </label>
                      ) : (
                        <button
                          onClick={() => toggleActive(type.id, type.is_active)}
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            type.is_active 
                              ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {type.is_active ? 'Active' : 'Inactive'}
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {editingType?.id === type.id ? (
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleUpdate(type.id)}
                            disabled={saving}
                            className="text-green-600 hover:text-green-900 disabled:opacity-50"
                          >
                            <Save className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setEditingType(null)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => setEditingType(type)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(type.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create Share Type</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={newType.name}
                    onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Ordinary Shares, Preference Shares"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newType.description}
                    onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Brief description of this share type"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority (Display Order)
                  </label>
                  <input
                    type="number"
                    value={newType.priority}
                    onChange={(e) => setNewType({ ...newType, priority: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                </div>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newType.is_active}
                    onChange={(e) => setNewType({ ...newType, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active (visible to users)</span>
                </label>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={saving || !newType.name.trim()}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareTypesPage;

